import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import SEOHelmet from '../seo/SEOHelmet';
import HeaderBanner from '../components/ui/HeaderBanner';
import { useLanguage } from '../i18n/LanguageContext';

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: 2.8rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  display: inline-block;
  left: 50%;
  transform: translateX(-50%);
  
  &:after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 0;
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-primary), transparent);
    left: 50%;
    transform: translateX(-50%);
  }
`;

const Heading = styled(motion.h2)`
  font-size: 1.8rem;
  margin: 2.5rem 0 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const SubHeading = styled(motion.h3)`
  font-size: 1.4rem;
  margin: 1.5rem 0 1rem;
  color: var(--text-primary);
`;

const Paragraph = styled(motion.p)`
  margin-bottom: 1.5rem;
  line-height: 1.8;
  color: var(--text-secondary);
  font-size: 1.05rem;
`;

const List = styled(motion.ul)`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled(motion.li)`
  margin-bottom: 0.8rem;
  line-height: 1.6;
  color: var(--text-secondary);
`;

const LastUpdated = styled.p`
  margin-top: 4rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
  font-style: italic;
  font-size: 0.9rem;
`;

const PrivacyPolicy: React.FC = () => {
  const { language } = useLanguage();
  
  const isItalian = language === 'it';
  const lastUpdated = isItalian ? 'Ultimo aggiornamento: 20 Luglio 2023' : 'Last updated: July 20, 2023';
  
  return (
    <>
      <SEOHelmet
        title={isItalian ? "Informativa sulla Privacy - J.O.H." : "Privacy Policy - J.O.H."}
        description={isItalian 
          ? "Informativa sulla Privacy di J.O.H. Gestione Immobiliare di Lusso. Come raccogliamo, utilizziamo e proteggiamo i tuoi dati personali." 
          : "Privacy Policy of J.O.H. Luxury Property Management. How we collect, use, and protect your personal data."}
        canonical="/privacy-policy"
      />
      
      <HeaderBanner />
      
      <Section padding="5rem 0">
        <ContentContainer>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {isItalian ? "Informativa sulla Privacy" : "Privacy Policy"}
          </Title>
          
          <Paragraph>
            {isItalian 
              ? "In J.O.H. rispettiamo la tua privacy e ci impegniamo a proteggere i tuoi dati personali. Questa informativa sulla privacy ti informerà su come ci prendiamo cura dei tuoi dati personali quando visiti il nostro sito web e ti informerà sui tuoi diritti sulla privacy e su come la legge ti protegge."
              : "At J.O.H., we respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "1. Raccolta di Informazioni" : "1. Information Collection"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Raccogliamo diversi tipi di informazioni per vari scopi al fine di fornirti e migliorare il nostro servizio."
              : "We collect several different types of information for various purposes to provide and improve our service to you."}
          </Paragraph>
          
          <SubHeading>
            {isItalian ? "1.1 Dati Personali" : "1.1 Personal Data"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "Durante l'utilizzo del nostro servizio, potremmo chiederti di fornirci alcune informazioni personali identificabili che possono essere utilizzate per contattarti o identificarti. Le informazioni personali identificabili possono includere, ma non sono limitate a:"
              : "While using our service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Personally identifiable information may include, but is not limited to:"}
          </Paragraph>
          <List>
            <ListItem>{isItalian ? "Nome e cognome" : "First and last name"}</ListItem>
            <ListItem>{isItalian ? "Indirizzo email" : "Email address"}</ListItem>
            <ListItem>{isItalian ? "Numero di telefono" : "Phone number"}</ListItem>
            <ListItem>{isItalian ? "Indirizzo, città, provincia, CAP" : "Address, city, state, postal code"}</ListItem>
            <ListItem>{isItalian ? "Cookie e dati di utilizzo" : "Cookies and usage data"}</ListItem>
          </List>
          
          <SubHeading>
            {isItalian ? "1.2 Dati di Utilizzo" : "1.2 Usage Data"}
          </SubHeading>
          <Paragraph>
            {isItalian 
              ? "Possiamo anche raccogliere informazioni su come il servizio viene utilizzato. Questi dati di utilizzo possono includere informazioni come l'indirizzo IP del tuo computer, il tipo di browser, la versione del browser, le pagine del nostro servizio che visiti, l'ora e la data della tua visita, il tempo trascorso su quelle pagine, identificatori unici del dispositivo e altri dati diagnostici."
              : "We may also collect information on how the service is accessed and used. This usage data may include information such as your computer's Internet Protocol address, browser type, browser version, the pages of our service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers, and other diagnostic data."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "2. Utilizzo dei Dati" : "2. Use of Data"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "J.O.H. utilizza i dati raccolti per vari scopi:"
              : "J.O.H. uses the collected data for various purposes:"}
          </Paragraph>
          <List>
            <ListItem>{isItalian ? "Per fornire e mantenere il nostro servizio" : "To provide and maintain our service"}</ListItem>
            <ListItem>{isItalian ? "Per informarti su cambiamenti nel nostro servizio" : "To notify you about changes to our service"}</ListItem>
            <ListItem>{isItalian ? "Per permetterti di partecipare a funzionalità interattive del nostro servizio quando decidi di farlo" : "To allow you to participate in interactive features of our service when you choose to do so"}</ListItem>
            <ListItem>{isItalian ? "Per fornire assistenza clienti" : "To provide customer support"}</ListItem>
            <ListItem>{isItalian ? "Per raccogliere analisi o informazioni preziose in modo da migliorare il nostro servizio" : "To gather analysis or valuable information so that we can improve our service"}</ListItem>
            <ListItem>{isItalian ? "Per monitorare l'utilizzo del nostro servizio" : "To monitor the usage of our service"}</ListItem>
            <ListItem>{isItalian ? "Per rilevare, prevenire e risolvere problemi tecnici" : "To detect, prevent and address technical issues"}</ListItem>
          </List>
          
          <Heading>
            {isItalian ? "3. Conservazione dei Dati" : "3. Data Retention"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "J.O.H. conserverà i tuoi dati personali solo per il tempo necessario agli scopi indicati in questa informativa sulla privacy. Conserveremo e utilizzeremo i tuoi dati personali nella misura necessaria per adempiere ai nostri obblighi legali, risolvere controversie e applicare i nostri accordi e politiche legali."
              : "J.O.H. will retain your personal data only for as long as is necessary for the purposes set out in this privacy policy. We will retain and use your personal data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "4. Trasferimento dei Dati" : "4. Transfer of Data"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Le tue informazioni, compresi i dati personali, possono essere trasferite a - e mantenute su - computer situati al di fuori del tuo stato, provincia, nazione o altra giurisdizione governativa dove le leggi sulla protezione dei dati possono essere diverse da quelle della tua giurisdizione."
              : "Your information, including personal data, may be transferred to — and maintained on — computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction."}
          </Paragraph>
          <Paragraph>
            {isItalian 
              ? "Il tuo consenso a questa informativa sulla privacy seguito dalla tua presentazione di tali informazioni rappresenta il tuo accordo al suddetto trasferimento."
              : "Your consent to this privacy policy followed by your submission of such information represents your agreement to that transfer."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "5. Divulgazione dei Dati" : "5. Disclosure of Data"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "J.O.H. può divulgare i tuoi dati personali in buona fede credendo che tale azione sia necessaria per:"
              : "J.O.H. may disclose your personal data in the good faith belief that such action is necessary to:"}
          </Paragraph>
          <List>
            <ListItem>{isItalian ? "Rispettare un obbligo legale" : "Comply with a legal obligation"}</ListItem>
            <ListItem>{isItalian ? "Proteggere e difendere i diritti o la proprietà di J.O.H." : "Protect and defend the rights or property of J.O.H."}</ListItem>
            <ListItem>{isItalian ? "Prevenire o investigare possibili illeciti in relazione al servizio" : "Prevent or investigate possible wrongdoing in connection with the service"}</ListItem>
            <ListItem>{isItalian ? "Proteggere la sicurezza personale degli utenti del servizio o del pubblico" : "Protect the personal safety of users of the service or the public"}</ListItem>
            <ListItem>{isItalian ? "Proteggere contro la responsabilità legale" : "Protect against legal liability"}</ListItem>
          </List>
          
          <Heading>
            {isItalian ? "6. Sicurezza dei Dati" : "6. Data Security"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "La sicurezza dei tuoi dati è importante per noi, ma ricorda che nessun metodo di trasmissione su Internet o metodo di archiviazione elettronica è sicuro al 100%. Mentre ci impegniamo a utilizzare mezzi commercialmente accettabili per proteggere i tuoi dati personali, non possiamo garantirne la sicurezza assoluta."
              : "The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee its absolute security."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "7. I Tuoi Diritti sulla Protezione dei Dati" : "7. Your Data Protection Rights"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Ai sensi del Regolamento Generale sulla Protezione dei Dati (GDPR), hai i seguenti diritti:"
              : "Under the General Data Protection Regulation (GDPR), you have the following rights:"}
          </Paragraph>
          <List>
            <ListItem>{isItalian ? "Il diritto di accesso: hai il diritto di richiedere copie dei tuoi dati personali." : "The right to access: You have the right to request copies of your personal data."}</ListItem>
            <ListItem>{isItalian ? "Il diritto di rettifica: hai il diritto di richiedere che correggiamo qualsiasi informazione che ritieni essere imprecisa. Hai anche il diritto di richiedere che completiamo le informazioni che ritieni incomplete." : "The right to rectification: You have the right to request that we correct any information you believe is inaccurate. You also have the right to request that we complete information you believe is incomplete."}</ListItem>
            <ListItem>{isItalian ? "Il diritto alla cancellazione: hai il diritto di richiedere che cancelliamo i tuoi dati personali, a determinate condizioni." : "The right to erasure: You have the right to request that we erase your personal data, under certain conditions."}</ListItem>
            <ListItem>{isItalian ? "Il diritto di limitare il trattamento: hai il diritto di richiedere che limitiamo il trattamento dei tuoi dati personali, a determinate condizioni." : "The right to restrict processing: You have the right to request that we restrict the processing of your personal data, under certain conditions."}</ListItem>
            <ListItem>{isItalian ? "Il diritto di opporsi al trattamento: hai il diritto di opporti al nostro trattamento dei tuoi dati personali, a determinate condizioni." : "The right to object to processing: You have the right to object to our processing of your personal data, under certain conditions."}</ListItem>
            <ListItem>{isItalian ? "Il diritto alla portabilità dei dati: hai il diritto di richiedere che trasferiamo i dati che abbiamo raccolto a un'altra organizzazione, o direttamente a te, a determinate condizioni." : "The right to data portability: You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions."}</ListItem>
          </List>
          
          <Heading>
            {isItalian ? "8. Link ad Altri Siti" : "8. Links to Other Sites"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Il nostro servizio può contenere link ad altri siti che non sono gestiti da noi. Se clicchi su un link di terze parti, sarai indirizzato al sito di quella terza parte. Ti consigliamo vivamente di rivedere l'informativa sulla privacy di ogni sito che visiti."
              : "Our service may contain links to other sites that are not operated by us. If you click on a third-party link, you will be directed to that third party's site. We strongly advise you to review the privacy policy of every site you visit."}
          </Paragraph>
          <Paragraph>
            {isItalian 
              ? "Non abbiamo alcun controllo e non ci assumiamo alcuna responsabilità per il contenuto, le politiche sulla privacy o le pratiche di siti o servizi di terze parti."
              : "We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "9. Modifiche a Questa Informativa sulla Privacy" : "9. Changes to This Privacy Policy"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Possiamo aggiornare la nostra informativa sulla privacy di tanto in tanto. Ti informeremo di eventuali modifiche pubblicando la nuova informativa sulla privacy in questa pagina."
              : "We may update our privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page."}
          </Paragraph>
          
          <Heading>
            {isItalian ? "10. Contattaci" : "10. Contact Us"}
          </Heading>
          <Paragraph>
            {isItalian 
              ? "Per qualsiasi domanda riguardante questa informativa sulla privacy, puoi contattarci tramite la nostra pagina di contatto."
              : "If you have any questions about this privacy policy, please contact us through our contact page."}
          </Paragraph>
          
          <LastUpdated>{lastUpdated}</LastUpdated>
        </ContentContainer>
      </Section>
    </>
  );
};

export default PrivacyPolicy; 